import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Droplets,
  Trash2,
  Edit,
  Info,
  MoreHorizontal,
  Archive,
  Plus,
} from 'lucide-react';
import MessageBubble from '@/components/MessageBubble';
import CareTipCard from '@/components/CareTipCard';
import PlantAvatar from '@/components/PlantAvatar';
import WateringProgress from '@/components/WateringProgress';
import LoadingSpinner from '@/components/LoadingSpinner';
import CareActionSheet from '@/components/CareActionSheet';
import PhotoPicker from '@/components/PhotoPicker';
import CareScheduleEditor from '@/components/CareScheduleEditor';
import {
  useTelegramHaptic,
  useTelegramBackButton,
  useTelegramMainButton,
  useTelegramPopup,
  useTelegram,
} from '@/hooks/telegram';
import { useStore } from '@/hooks/useStore';
import * as api from '@/utils/api';
import { everyNDays } from '@/utils/date';
import type { Plant, PlantMessage, CareAction, CareActionType, JournalEntry } from '@/types';

type Tab = 'chat' | 'care' | 'journal';

export default function PlantPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isTelegramWebApp } = useTelegram();
  const haptic = useTelegramHaptic();
  const { showConfirm } = useTelegramPopup();
  const deletePlantFromStore = useStore((state) => state.deletePlant);
  const archivePlantFromStore = useStore((state) => state.archivePlant);
  const syncPlant = useStore((state) => state.syncPlant);
  const fetchUser = useStore((state) => state.fetchUser);
  const [activeTab, setActiveTab] = useState<Tab>('chat');
  const [showMenu, setShowMenu] = useState(false);
  const [isWatering, setIsWatering] = useState(false);
  const [showCareSheet, setShowCareSheet] = useState(false);
  const [showScheduleEditor, setShowScheduleEditor] = useState(false);
  const [isCareSubmitting, setIsCareSubmitting] = useState(false);

  // Local state instead of global store
  const [plant, setPlant] = useState<Plant | null>(null);
  const [messages, setMessages] = useState<PlantMessage[]>([]);
  const [careHistory, setCareHistory] = useState<CareAction[]>([]);
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [journalText, setJournalText] = useState('');
  const [journalHeight, setJournalHeight] = useState('');
  const [journalPhoto, setJournalPhoto] = useState<File | null>(null);
  const [isSubmittingJournal, setIsSubmittingJournal] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [journalMenuEntryId, setJournalMenuEntryId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const [showJournalForm, setShowJournalForm] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      if (!id) return;

      setIsLoading(true);
      setError(null);

      try {
        const [plantData, messagesData, historyData, journalData] = await Promise.all([
          api.getPlant(id),
          api.getPlantMessages(id),
          api.getCareHistory(id),
          api.getJournalEntries(id),
        ]);

        if (!cancelled) {
          setPlant(plantData);
          setMessages(messagesData);
          setCareHistory(historyData);
          setJournalEntries(journalData);
          setHasMoreMessages(messagesData.length >= 50);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Не удалось загрузить растение');
          setIsLoading(false);
        }
      }
    }

    loadData();

    return () => {
      cancelled = true;
    };
  }, [id]);

  // Mark messages as read when viewing chat tab
  const clearUnreadCount = useStore((s) => s.clearUnreadCount);
  useEffect(() => {
    if (activeTab === 'chat' && id && messages.length > 0) {
      api.markMessagesAsRead(id).then(() => {
        clearUnreadCount(id);
      }).catch(() => {});
    }
  }, [activeTab, id, messages.length, clearUnreadCount]);

  const handleBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Use Telegram BackButton
  useTelegramBackButton(handleBack);

  const handleWater = useCallback(async () => {
    if (!plant || isWatering) return;

    haptic('medium');
    setIsWatering(true);

    try {
      await api.recordCareAction(plant.id, { actionType: 'WATERING' });
      // Refresh data
      const [plantData, messagesData, historyData] = await Promise.all([
        api.getPlant(plant.id),
        api.getPlantMessages(plant.id),
        api.getCareHistory(plant.id),
      ]);
      setPlant(plantData);
      setMessages(messagesData);
      setCareHistory(historyData);
      syncPlant(plantData);
      await fetchUser();
      haptic('success');
    } catch {
      haptic('error');
    } finally {
      setIsWatering(false);
    }
  }, [plant, isWatering, haptic, syncPlant, fetchUser]);

  const loadMoreMessages = useCallback(async () => {
    if (!plant || isLoadingMoreMessages || !hasMoreMessages) return;
    setIsLoadingMoreMessages(true);
    try {
      const lastMsg = messages[messages.length - 1];
      const cursor = lastMsg?.createdAt;
      const more = await api.getPlantMessages(plant.id, 50, cursor);
      setMessages((prev) => [...prev, ...more]);
      setHasMoreMessages(more.length >= 50);
    } catch {
      // silently fail
    } finally {
      setIsLoadingMoreMessages(false);
    }
  }, [plant, messages, isLoadingMoreMessages, hasMoreMessages]);

  const handleDelete = async () => {
    if (!plant) return;

    const confirmed = await showConfirm(
      `Вы уверены, что хотите удалить "${plant.nickname}"?`
    );

    if (confirmed) {
      haptic('medium');
      try {
        await deletePlantFromStore(plant.id);
        navigate('/');
      } catch {
        haptic('error');
      }
    }
  };

  const handleArchive = async () => {
    if (!plant) return;

    const confirmed = await showConfirm(
      `Архивировать "${plant.nickname}"? Растение можно будет восстановить.`
    );

    if (confirmed) {
      haptic('medium');
      try {
        await archivePlantFromStore(plant.id);
        navigate('/');
      } catch {
        haptic('error');
      }
    }
  };

  const handleCareAction = async (actionType: CareActionType, notes?: string) => {
    if (!plant) return;

    haptic('medium');
    setIsCareSubmitting(true);

    try {
      await api.recordCareAction(plant.id, { actionType, notes });
      const [plantData, messagesData, historyData] = await Promise.all([
        api.getPlant(plant.id),
        api.getPlantMessages(plant.id),
        api.getCareHistory(plant.id),
      ]);
      setPlant(plantData);
      setMessages(messagesData);
      setCareHistory(historyData);
      syncPlant(plantData);
      fetchUser();
      haptic('success');
      setShowCareSheet(false);
    } catch {
      haptic('error');
    } finally {
      setIsCareSubmitting(false);
    }
  };

  const handleScheduleUpdated = useCallback(async () => {
    if (!plant) return;
    try {
      const plantData = await api.getPlant(plant.id);
      setPlant(plantData);
      syncPlant(plantData);
    } catch {
      // Silently ignore — schedule was saved, just couldn't refresh
    }
  }, [plant, syncPlant]);

  const handleDeleteJournalEntry = async (entryId: string) => {
    if (!plant) return;

    const confirmed = await showConfirm('Удалить запись из журнала?');
    if (!confirmed) return;

    haptic('medium');
    try {
      await api.deleteJournalEntry(plant.id, entryId);
      setJournalEntries((prev) => prev.filter((e) => e.id !== entryId));
      haptic('success');
    } catch {
      haptic('error');
    }
  };

  const handleStartEditJournalEntry = (entry: JournalEntry) => {
    haptic('selection');
    setEditingEntryId(entry.id);
    setJournalText(entry.content);
    setJournalHeight(entry.heightCm !== null ? String(entry.heightCm) : '');
    setJournalPhoto(null);
    setJournalMenuEntryId(null);
  };

  const handleCancelEditJournal = () => {
    setEditingEntryId(null);
    setJournalText('');
    setJournalHeight('');
    setJournalPhoto(null);
  };

  const handleTabChange = (tab: Tab) => {
    haptic('selection');
    setActiveTab(tab);
  };

  // MainButton configuration for watering
  const status = (plant?.wateringStatus?.status || 'ok') as 'ok' | 'soon' | 'overdue' | 'critical';

  const mainButtonConfig = useMemo(() => {
    if (!plant || isLoading || showCareSheet || showScheduleEditor) return null;

    const buttonText = status === 'critical'
      ? 'Срочно полить!'
      : status === 'overdue'
      ? 'Пора полить'
      : status === 'soon'
      ? 'Полить сейчас'
      : 'Полить';

    const buttonColor = status === 'critical'
      ? '#ef4444'
      : status === 'overdue'
      ? '#f97316'
      : status === 'soon'
      ? '#3b82f6'
      : '#22c55e';

    return {
      text: buttonText,
      onClick: handleWater,
      isEnabled: !isWatering,
      showProgress: isWatering,
      color: buttonColor,
      textColor: '#ffffff',
    };
  }, [plant, status, isWatering, handleWater, isLoading, showCareSheet, showScheduleEditor]);

  // Use Telegram MainButton
  useTelegramMainButton(mainButtonConfig);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !plant) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        <p className="text-red-500 mb-4">{error || 'Растение не найдено'}</p>
        <button onClick={() => navigate('/')} className="tg-button">
          На главную
        </button>
      </div>
    );
  }

  // Combine messages and care actions for chat view
  const chatItems: Array<{ type: 'message' | 'action'; item: PlantMessage | CareAction; date: Date }> = [
    ...messages.map((m) => ({ type: 'message' as const, item: m, date: new Date(m.createdAt) })),
    ...careHistory.map((a) => ({ type: 'action' as const, item: a, date: new Date(a.createdAt) })),
  ].sort((a, b) => b.date.getTime() - a.date.getTime());

  return (
    <div className="flex-1 flex flex-col safe-area-top">
      {/* Header */}
      <div className="px-4 py-4 border-b border-[var(--sf-border-light)]">
        <div className="flex items-center gap-3">
          {/* Back button — only for non-Telegram browsers (Telegram has native BackButton) */}
          {!isTelegramWebApp && (
            <button
              onClick={() => navigate('/')}
              className="w-10 h-10 rounded-full glass-circle flex items-center justify-center"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}

          {/* Plant photo or avatar */}
          {plant.photoUrl ? (
            <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
              <img src={api.photoSrc(plant.photoUrl)} alt={plant.nickname} className="w-full h-full object-cover" />
            </div>
          ) : (
            <PlantAvatar
              speciesScientificName={plant.species?.scientificName}
              category={plant.species?.category}
              size="sm"
              status={status}
            />
          )}

          {/* Plant info */}
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold text-lg truncate">{plant.nickname}</h1>
            <p className="text-sm text-tg-hint truncate">
              {plant.species?.commonNameRu || plant.customSpecies || 'Неизвестный вид'}
            </p>
          </div>

          {/* Menu button */}
          <div className="relative">
            <button
              onClick={() => {
                haptic('light');
                setShowMenu(!showMenu);
              }}
              className="w-10 h-10 rounded-full glass-circle flex items-center justify-center"
            >
              <MoreHorizontal className="w-5 h-5" />
            </button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 top-12 glass-dropdown rounded-xl z-20 py-2 min-w-[180px]">
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      navigate(`/edit/${plant.id}`);
                    }}
                    className="w-full px-4 py-3 text-left flex items-center gap-3 active:bg-black/5 dark:active:bg-white/5 transition-colors"
                  >
                    <Edit className="w-5 h-5 text-tg-hint" />
                    <span>Редактировать</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      handleArchive();
                    }}
                    className="w-full px-4 py-3 text-left flex items-center gap-3 active:bg-black/5 dark:active:bg-white/5 transition-colors"
                  >
                    <Archive className="w-5 h-5 text-tg-hint" />
                    <span>Архивировать</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      handleDelete();
                    }}
                    className="w-full px-4 py-3 text-left flex items-center gap-3 active:bg-black/5 dark:active:bg-white/5 transition-colors text-red-500"
                  >
                    <Trash2 className="w-5 h-5" />
                    <span>Удалить</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Watering progress */}
        <div className="mt-3">
          <WateringProgress
            status={status}
            daysUntil={plant.wateringStatus?.daysUntil}
            message={plant.wateringStatus?.message}
            wateringFrequencyDays={plant.species?.wateringFrequencyDays}
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mt-4">
          {[
            { id: 'chat', label: 'Чат', icon: '💬' },
            { id: 'care', label: 'Уход', icon: '🌱' },
            { id: 'journal', label: 'Журнал', icon: '📔' },
          ].map(({ id, label, icon }) => (
            <button
              key={id}
              onClick={() => handleTabChange(id as Tab)}
              className={`flex-1 py-2 px-1 rounded-xl text-xs font-medium transition-all duration-200 flex flex-col items-center gap-0.5 ${
                activeTab === id
                  ? 'glass-circle-green text-plant-green-500'
                  : 'glass-circle text-tg-hint'
              }`}
            >
              <span className="text-base">{icon}</span>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {activeTab === 'chat' && (
          <div className="space-y-2">
            {chatItems.length > 0 ? (
              chatItems.map(({ type, item }) => (
                <MessageBubble
                  key={item.id}
                  message={type === 'message' ? (item as PlantMessage) : undefined}
                  action={type === 'action' ? (item as CareAction) : undefined}
                  plantName={plant.nickname}

                  speciesScientificName={plant.species?.scientificName}
                />
              ))
            ) : (
              <div className="text-center py-12 text-tg-hint">
                <div className="w-16 h-16 rounded-full glass-circle mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl">💬</span>
                </div>
                <p>Пока нет сообщений</p>
                <p className="text-sm mt-2">Полейте растение, и оно напишет вам!</p>
              </div>
            )}
            {hasMoreMessages && (
              <button
                onClick={loadMoreMessages}
                disabled={isLoadingMoreMessages}
                className="w-full py-3 rounded-xl glass-circle text-sm font-medium text-tg-hint active:scale-[0.98] transition-transform mt-2"
              >
                {isLoadingMoreMessages ? 'Загрузка...' : 'Загрузить ещё'}
              </button>
            )}
          </div>
        )}

        {activeTab === 'care' && (
          <div className="space-y-4">
            {/* Record care action button */}
            <button
              onClick={() => {
                haptic('light');
                setShowCareSheet(true);
              }}
              className="w-full py-3 rounded-xl bg-plant-green-500 text-white font-medium flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              <Plus className="w-5 h-5" />
              Записать уход
            </button>

            {/* Active care schedules */}
            {plant.careSchedules && plant.careSchedules.length > 0 && (
              <div>
                <h3 className="font-medium text-sm text-tg-hint uppercase tracking-wide mb-2">Расписания ухода</h3>
                <div className="space-y-2">
                  {plant.careSchedules.filter((s) => s.isEnabled).map((schedule) => {
                    const actionLabels: Record<string, { label: string; emoji: string }> = {
                      WATERING: { label: 'Полив', emoji: '💧' },
                      FERTILIZING: { label: 'Удобрение', emoji: '🧪' },
                      MISTING: { label: 'Опрыскивание', emoji: '💨' },
                      ROTATING: { label: 'Поворот', emoji: '🔄' },
                      REPOTTING: { label: 'Пересадка', emoji: '🪴' },
                      PRUNING: { label: 'Обрезка', emoji: '✂️' },
                      CLEANING: { label: 'Чистка', emoji: '🧹' },
                    };
                    const info = actionLabels[schedule.actionType] || { label: schedule.actionType, emoji: '📝' };
                    const daysUntil = schedule.nextDueAt
                      ? Math.ceil((new Date(schedule.nextDueAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                      : null;
                    const schedStatus = daysUntil === null ? 'ok'
                      : daysUntil < 0 ? 'overdue'
                      : daysUntil <= 1 ? 'soon'
                      : 'ok';

                    return (
                      <div key={schedule.id} className="tg-card flex items-center gap-3">
                        <span className="text-xl">{info.emoji}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{info.label}</p>
                          <p className="text-xs text-tg-hint">
                            {everyNDays(schedule.frequencyDays)}
                          </p>
                        </div>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          schedStatus === 'overdue' ? 'bg-red-500/10 text-red-500' :
                          schedStatus === 'soon' ? 'bg-yellow-500/10 text-yellow-500' :
                          'bg-green-500/10 text-green-500'
                        }`}>
                          {daysUntil === null ? '—' :
                           daysUntil < 0 ? `Просрочено на ${Math.abs(daysUntil)} дн.` :
                           daysUntil === 0 ? 'Сегодня' :
                           `Через ${daysUntil} дн.`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Schedule editor button */}
            <button
              onClick={() => {
                haptic('light');
                setShowScheduleEditor(true);
              }}
              className="w-full py-3 rounded-xl glass-circle text-sm font-medium text-plant-green-500 active:scale-[0.98] transition-transform"
            >
              ⚙️ Настроить расписание
            </button>

            {plant.species ? (
              <CareTipCard species={plant.species} />
            ) : (
              <div className="text-center py-12 text-tg-hint">
                <div className="w-16 h-16 rounded-full glass-circle mx-auto mb-4 flex items-center justify-center">
                  <Info className="w-8 h-8 opacity-50" />
                </div>
                <p>Советы по уходу недоступны</p>
                <p className="text-sm mt-2">
                  Выберите вид растения для получения рекомендаций
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'journal' && (
          <div className="space-y-4">
            {/* Journal entry form — collapsed by default */}
            {(showJournalForm || editingEntryId) ? (
              <div className="tg-card space-y-3">
                <div className="flex gap-3">
                  <PhotoPicker
                    photoUrl={null}
                    onPhotoSelected={(file) => setJournalPhoto(file)}
                    onPhotoRemoved={() => setJournalPhoto(null)}
                    size="sm"
                  />
                  <textarea
                    value={journalText}
                    onChange={(e) => setJournalText(e.target.value)}
                    placeholder="Заметка о растении..."
                    className="tg-input min-h-[80px] resize-none flex-1"
                    maxLength={2000}
                  />
                </div>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    inputMode="decimal"
                    value={journalHeight}
                    onChange={(e) => setJournalHeight(e.target.value)}
                    placeholder="Высота (см)"
                    className="tg-input flex-1"
                    min="0"
                    step="0.1"
                  />
                  <button
                    onClick={() => {
                      handleCancelEditJournal();
                      setShowJournalForm(false);
                    }}
                    className="px-3 py-2 rounded-xl glass-circle text-sm font-medium text-tg-hint active:scale-95 transition-transform"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={async () => {
                      if (!plant || !journalText.trim()) return;
                      setIsSubmittingJournal(true);
                      try {
                        if (editingEntryId) {
                          const updated = await api.updateJournalEntry(plant.id, editingEntryId, {
                            content: journalText.trim(),
                            heightCm: journalHeight ? parseFloat(journalHeight) : null,
                          });
                          setJournalEntries((prev) =>
                            prev.map((e) => (e.id === editingEntryId ? updated : e))
                          );
                          setEditingEntryId(null);
                        } else {
                          let photoUrl: string | undefined;
                          if (journalPhoto) {
                            photoUrl = await api.uploadJournalPhoto(plant.id, journalPhoto);
                          }
                          const entry = await api.createJournalEntry(plant.id, {
                            content: journalText.trim(),
                            heightCm: journalHeight ? parseFloat(journalHeight) : undefined,
                            photoUrl,
                          });
                          setJournalEntries((prev) => [entry, ...prev]);
                        }
                        setJournalText('');
                        setJournalHeight('');
                        setJournalPhoto(null);
                        setShowJournalForm(false);
                        haptic('success');
                      } catch {
                        haptic('error');
                      } finally {
                        setIsSubmittingJournal(false);
                      }
                    }}
                    disabled={!journalText.trim() || isSubmittingJournal}
                    className="px-4 py-2 rounded-xl bg-plant-green-500 text-white text-sm font-medium disabled:opacity-50 active:scale-95 transition-transform"
                  >
                    {isSubmittingJournal ? '...' : editingEntryId ? 'Сохранить' : 'Добавить'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  haptic('light');
                  setShowJournalForm(true);
                }}
                className="w-full py-3 rounded-xl glass-circle text-sm font-medium text-plant-green-500 flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
              >
                <Plus className="w-4 h-4" />
                Добавить запись
              </button>
            )}

            {/* Growth chart (simple) */}
            {(() => {
              const heightEntries = journalEntries.filter((e) => e.heightCm !== null).reverse();
              if (heightEntries.length < 2) return null;
              const maxH = Math.max(...heightEntries.map((e) => e.heightCm!));
              const minH = Math.min(...heightEntries.map((e) => e.heightCm!));
              const range = maxH - minH || 1;
              return (
                <div className="tg-card">
                  <h3 className="font-medium text-sm mb-2">График роста</h3>
                  <div className="flex items-end gap-1 h-24">
                    {heightEntries.map((e) => (
                      <div key={e.id} className="flex-1 flex flex-col items-center justify-end">
                        <div
                          className="w-full rounded-t bg-plant-green-500/70 min-h-[4px]"
                          style={{ height: `${((e.heightCm! - minH) / range) * 80 + 20}%` }}
                        />
                        <span className="text-[9px] text-tg-hint mt-1">{e.heightCm}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-tg-hint mt-1 text-right">{minH} — {maxH} см</p>
                </div>
              );
            })()}

            {/* Journal entries timeline */}
            <div className="space-y-2">
              {journalEntries.length > 0 ? (
                journalEntries.map((entry) => (
                  <div key={entry.id} className="tg-card">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-tg-hint flex-1">
                        {new Date(entry.createdAt).toLocaleDateString('ru-RU', {
                          day: 'numeric', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })}
                        {entry.heightCm !== null && (
                          <span className="text-plant-green-500 font-medium ml-2">
                            {entry.heightCm} см
                          </span>
                        )}
                      </span>
                      <div className="relative">
                        <button
                          onClick={() => {
                            haptic('light');
                            setJournalMenuEntryId(
                              journalMenuEntryId === entry.id ? null : entry.id
                            );
                          }}
                          className="w-9 h-9 rounded-full flex items-center justify-center -mr-1 active:bg-black/5 dark:active:bg-white/5 transition-colors"
                        >
                          <MoreHorizontal className="w-4 h-4 text-tg-hint" />
                        </button>
                        {journalMenuEntryId === entry.id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setJournalMenuEntryId(null)}
                            />
                            <div className="absolute right-0 top-8 glass-dropdown rounded-xl z-20 py-1 min-w-[160px]">
                              <button
                                onClick={() => handleStartEditJournalEntry(entry)}
                                className="w-full px-4 py-2.5 text-left flex items-center gap-3 active:bg-black/5 dark:active:bg-white/5 transition-colors text-sm"
                              >
                                <Edit className="w-4 h-4 text-tg-hint" />
                                <span>Редактировать</span>
                              </button>
                              <button
                                onClick={() => {
                                  setJournalMenuEntryId(null);
                                  handleDeleteJournalEntry(entry.id);
                                }}
                                className="w-full px-4 py-2.5 text-left flex items-center gap-3 active:bg-black/5 dark:active:bg-white/5 transition-colors text-sm text-red-500"
                              >
                                <Trash2 className="w-4 h-4" />
                                <span>Удалить</span>
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <p className="text-sm">{entry.content}</p>
                    {entry.photoUrl && (
                      <img src={api.photoSrc(entry.photoUrl)} alt="" className="mt-2 rounded-lg max-h-48 object-cover" />
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-tg-hint">
                  <p>Журнал пуст</p>
                  <p className="text-sm mt-1">Записывайте наблюдения за растением!</p>
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Fallback water button for non-Telegram browsers */}
      {!isTelegramWebApp && (
        <div className="px-4 py-4 border-t border-[var(--sf-border-light)] safe-area-bottom">
          <button
            onClick={handleWater}
            disabled={isWatering}
            className={`w-full py-4 rounded-2xl font-medium flex items-center justify-center gap-3 transition-all ${
              isWatering ? 'opacity-70' : 'active:scale-[0.98]'
            }`}
            style={{
              background: status === 'critical'
                ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                : status === 'overdue'
                ? 'linear-gradient(135deg, #f97316, #ea580c)'
                : status === 'soon'
                ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
                : 'var(--sf-bg-card)',
              border: status === 'ok' ? '1px solid var(--sf-border-light)' : 'none',
              color: status === 'ok' ? 'var(--tg-theme-text-color)' : 'white',
              boxShadow: status === 'critical'
                ? '0 8px 32px rgba(239, 68, 68, 0.4)'
                : status !== 'ok'
                ? '0 8px 32px rgba(0, 0, 0, 0.15)'
                : 'var(--sf-shadow-sm)',
            }}
          >
            {isWatering ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <Droplets className="w-6 h-6" />
                <span>
                  {status === 'critical'
                    ? 'Срочно полить!'
                    : status === 'overdue'
                    ? 'Пора полить'
                    : status === 'soon'
                    ? 'Полить сейчас'
                    : 'Полить'}
                </span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Care action sheet */}
      <CareActionSheet
        isOpen={showCareSheet}
        onClose={() => setShowCareSheet(false)}
        onSubmit={handleCareAction}
        isSubmitting={isCareSubmitting}
      />

      {/* Care schedule editor */}
      <CareScheduleEditor
        isOpen={showScheduleEditor}
        onClose={() => setShowScheduleEditor(false)}
        plantId={plant.id}
        existingSchedules={plant.careSchedules || []}
        onUpdated={handleScheduleUpdated}
      />
    </div>
  );
}
